import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { BrandHeader } from './BrandHeader';
import { Spinner } from './Spinner';

type Status = 'loading' | 'succeeded' | 'processing' | 'failed' | 'unknown';

function mapPaymentStatus(status?: string): Status {
  switch (status) {
    case 'succeeded':
      return 'succeeded';
    case 'processing':
      return 'processing';
    case 'requires_payment_method':
    case 'canceled':
      return 'failed';
    default:
      return 'unknown';
  }
}

const COPY: Record<Exclude<Status, 'loading'>, { icon: string; cls: string; title: string; body: string }> = {
  succeeded: {
    icon: '✓',
    cls: 'ok',
    title: "You're all set.",
    body: 'Your subscription is active. A welcome email with your receipt is on its way to your inbox.',
  },
  processing: {
    icon: '⏳',
    cls: 'warn',
    title: 'Payment processing',
    body: 'Your payment is being processed. You will receive a confirmation email once it completes.',
  },
  failed: {
    icon: '✕',
    cls: 'err',
    title: 'Payment not completed',
    body: 'We could not complete your payment. Please return to checkout and try again.',
  },
  unknown: {
    icon: '?',
    cls: 'warn',
    title: 'Thanks!',
    body: 'We received your request. If anything looks off, please contact support.',
  },
};

export function SuccessPage() {
  const [status, setStatus] = useState<Status>('loading');
  const [siteUrl, setSiteUrl] = useState('https://www.gorillabow.com/');
  const [supportEmail, setSupportEmail] = useState('support@gorillabow.com');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.getConfig();
        if (cancelled) return;
        setSiteUrl(cfg.brand.siteUrl);
        setSupportEmail(cfg.brand.supportEmail);

        const params = new URLSearchParams(window.location.search);
        const piSecret = params.get('payment_intent_client_secret');
        const siSecret = params.get('setup_intent_client_secret');
        if (!piSecret && !siSecret) {
          setStatus('succeeded');
          return;
        }

        const stripe = await loadStripe(cfg.publishableKey);
        if (!stripe) {
          setStatus('unknown');
          return;
        }
        if (piSecret) {
          const { paymentIntent } = await stripe.retrievePaymentIntent(piSecret);
          if (!cancelled) setStatus(mapPaymentStatus(paymentIntent?.status));
        } else if (siSecret) {
          const { setupIntent } = await stripe.retrieveSetupIntent(siSecret);
          if (!cancelled) setStatus(setupIntent?.status === 'succeeded' ? 'succeeded' : 'processing');
        }
      } catch {
        if (!cancelled) setStatus('unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page">
      <BrandHeader siteUrl={siteUrl} />
      <div className="card">
        {status === 'loading' ? (
          <Spinner label="Confirming your payment…" />
        ) : (
          <div className="card-section center">
            <div className={`status-icon ${COPY[status].cls}`}>{COPY[status].icon}</div>
            <h1 className="product-name">{COPY[status].title}</h1>
            <p className="product-desc" style={{ marginTop: 12 }}>
              {COPY[status].body}
            </p>
            {status === 'failed' ? (
              <a className="btn btn-primary mt-24" href="/" style={{ display: 'inline-block' }}>
                Back to checkout
              </a>
            ) : (
              <a
                className="btn btn-primary mt-24"
                href={siteUrl}
                style={{ display: 'inline-block' }}
                target="_blank"
                rel="noreferrer"
              >
                Open Gorilla Bow
              </a>
            )}
          </div>
        )}
      </div>
      <p className="footer-note">
        Need help? Contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
      </p>
    </div>
  );
}
